import { createContext, type ReactNode } from "react";

/**
 * 확인창 종류 — 색은 제목·영향 강조·확인 버튼에 함께 쓴다(2026-10-13 기준).
 *   danger  빨강: 삭제·영구 제거·거부처럼 되돌릴 수 없는 파괴적 작업
 *   warning 주황: 기존 값을 바꾸거나 되돌리기 어려운 변경(덮어쓰기·적용·발송·미저장 변경 버리기·재시작)
 *   info    기본: 일반 진행(신규 저장·동기화·로그아웃 등). 제목은 중립색
 */
export type ConfirmKind = "info" | "warning" | "danger";

/** 영향 목록의 한 줄. 문자열은 보조 톤 한 줄로 표시한다. */
export type ConfirmEffect = string | {
  text: ReactNode;
  /** 핵심 영향 — kind 색 아이콘 + 진한 글씨. 한 창에 1개를 권장 */
  strong?: boolean;
  /** Icon 이름. 없으면 strong 은 kind 기본 아이콘, 보조 줄은 작은 점 */
  icon?: string;
};

export interface ConfirmOptions {
  /** 질문형 제목 — "배합을 삭제할까요?" */
  title?: string;
  /** 대상·수치 한 줄 — "대상 3건 · 출금 120,000원" */
  summary?: ReactNode;
  /** 무슨 일이 일어나는지 2~4줄. 11개 이상이면 10개 + "외 N건 더 보기" */
  effects?: ConfirmEffect[];
  /** "되돌릴 수 없습니다." 핵심 줄을 영향 목록 끝에 붙인다 */
  irreversible?: boolean;
  /** 목록으로 표현할 수 없는 추가 내용(드묾) */
  body?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  kind?: ConfirmKind;
}

export interface ConfirmApi {
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
}

export const ConfirmContext = createContext<ConfirmApi | null>(null);

/** Provider 가 없을 때 window.confirm 으로 보여 줄 평문. */
export function confirmPlainText(opts: ConfirmOptions): string {
  const lines: string[] = [];
  if (opts.title) lines.push(opts.title);
  if (typeof opts.summary === "string") lines.push(opts.summary);
  for (const e of opts.effects ?? []) {
    const text = typeof e === "string" ? e : e.text;
    if (typeof text === "string") lines.push(`- ${text}`);
  }
  if (opts.irreversible) lines.push("- 되돌릴 수 없습니다.");
  return lines.join("\n") || "확인하시겠습니까?";
}
